export interface TemplateTask {
  room: string;
  text: string;
  task_type: "clean";
}

export interface ChecklistTemplate {
  id: string;
  name: string;
  description: string;
  tasks: TemplateTask[];
}

const livingRoomTasks: TemplateTask[] = [
  { room: "Living Room", text: "Dust all surfaces", task_type: "clean" },
  { room: "Living Room", text: "Vacuum floor and furniture", task_type: "clean" },
  { room: "Living Room", text: "Wipe light switches and remotes", task_type: "clean" },
  { room: "Living Room", text: "Straighten cushions and throws", task_type: "clean" },
  { room: "Living Room", text: "Empty trash", task_type: "clean" },
  { room: "Living Room", text: "Check for guest belongings", task_type: "clean" },
  { room: "Living Room", text: "Wipe coffee table and side tables", task_type: "clean" },
  { room: "Living Room", text: "Fluff decorative pillows", task_type: "clean" },
];

const kitchenTasks: TemplateTask[] = [
  { room: "Kitchen", text: "Wipe all countertops", task_type: "clean" },
  { room: "Kitchen", text: "Clean stovetop and burners", task_type: "clean" },
  { room: "Kitchen", text: "Wipe microwave inside and out", task_type: "clean" },
  { room: "Kitchen", text: "Run/empty dishwasher", task_type: "clean" },
  { room: "Kitchen", text: "Wipe cabinet fronts", task_type: "clean" },
  { room: "Kitchen", text: "Clean sink and faucet", task_type: "clean" },
  { room: "Kitchen", text: "Restock dish soap and sponge", task_type: "clean" },
  { room: "Kitchen", text: "Check inside fridge for old items", task_type: "clean" },
  { room: "Kitchen", text: "Take out trash and replace bag", task_type: "clean" },
  { room: "Kitchen", text: "Sweep and mop floor", task_type: "clean" },
  { room: "Kitchen", text: "Wipe appliance handles", task_type: "clean" },
];

const masterBedroomTasks: TemplateTask[] = [
  { room: "Master Bedroom", text: "Strip bed and replace with fresh linens", task_type: "clean" },
  { room: "Master Bedroom", text: "Make bed to hotel standard", task_type: "clean" },
  { room: "Master Bedroom", text: "Dust all surfaces", task_type: "clean" },
  { room: "Master Bedroom", text: "Vacuum floor", task_type: "clean" },
  { room: "Master Bedroom", text: "Empty trash", task_type: "clean" },
  { room: "Master Bedroom", text: "Check under bed", task_type: "clean" },
  { room: "Master Bedroom", text: "Wipe mirrors", task_type: "clean" },
  { room: "Master Bedroom", text: "Restock any amenities", task_type: "clean" },
  { room: "Master Bedroom", text: "Check hangers and closet", task_type: "clean" },
];

const masterBathroomTasks: TemplateTask[] = [
  { room: "Master Bathroom", text: "Clean toilet (bowl, seat, tank, base)", task_type: "clean" },
  { room: "Master Bathroom", text: "Clean sink and faucet", task_type: "clean" },
  { room: "Master Bathroom", text: "Clean shower/tub", task_type: "clean" },
  { room: "Master Bathroom", text: "Wipe mirrors", task_type: "clean" },
  { room: "Master Bathroom", text: "Restock toilet paper (minimum 4 rolls)", task_type: "clean" },
  { room: "Master Bathroom", text: "Restock hand soap", task_type: "clean" },
  { room: "Master Bathroom", text: "Restock shampoo/conditioner/body wash", task_type: "clean" },
  { room: "Master Bathroom", text: "Replace bath mat if needed", task_type: "clean" },
  { room: "Master Bathroom", text: "Empty trash", task_type: "clean" },
  { room: "Master Bathroom", text: "Sweep and mop floor", task_type: "clean" },
];

const guestBedroomTasks: TemplateTask[] = masterBedroomTasks.map((t) => ({
  ...t,
  room: "Guest Bedroom",
}));

const guestBathroomTasks: TemplateTask[] = masterBathroomTasks.map((t) => ({
  ...t,
  room: "Guest Bathroom",
}));

const laundryTasks: TemplateTask[] = [
  { room: "Laundry", text: "Run washer/dryer if needed", task_type: "clean" },
  { room: "Laundry", text: "Clean lint trap", task_type: "clean" },
  { room: "Laundry", text: "Wipe surfaces", task_type: "clean" },
  { room: "Laundry", text: "Restock laundry pods if provided", task_type: "clean" },
];

const finalTasks: TemplateTask[] = [
  { room: "General/Final", text: "Walk through all rooms", task_type: "clean" },
  { room: "General/Final", text: "Lock all windows and doors", task_type: "clean" },
  { room: "General/Final", text: "Set thermostat", task_type: "clean" },
  { room: "General/Final", text: "Take photos for record", task_type: "clean" },
  { room: "General/Final", text: "Report any damage", task_type: "clean" },
];

export const TURNOVER_CLEAN_TASKS: TemplateTask[] = [
  ...livingRoomTasks,
  ...kitchenTasks,
  ...masterBedroomTasks,
  ...masterBathroomTasks,
  ...guestBedroomTasks,
  ...guestBathroomTasks,
  ...laundryTasks,
  ...finalTasks,
];

const deepCleanExtras: TemplateTask[] = [
  { room: "Kitchen", text: "Clean inside oven", task_type: "clean" },
  { room: "Kitchen", text: "Clean inside fridge", task_type: "clean" },
  { room: "Kitchen", text: "Clean behind appliances", task_type: "clean" },
  { room: "Living Room", text: "Wipe baseboards", task_type: "clean" },
  { room: "Master Bedroom", text: "Wipe baseboards", task_type: "clean" },
  { room: "Guest Bedroom", text: "Wipe baseboards", task_type: "clean" },
  { room: "Living Room", text: "Clean windows interior", task_type: "clean" },
  { room: "Master Bedroom", text: "Dust ceiling fans", task_type: "clean" },
  { room: "Guest Bedroom", text: "Dust ceiling fans", task_type: "clean" },
  { room: "Master Bathroom", text: "Descale faucets and showerhead", task_type: "clean" },
  { room: "Guest Bathroom", text: "Descale faucets and showerhead", task_type: "clean" },
  { room: "Kitchen", text: "Degrease range hood filter area", task_type: "clean" },
  { room: "Master Bathroom", text: "Scrub grout lines", task_type: "clean" },
  { room: "Guest Bathroom", text: "Scrub grout lines", task_type: "clean" },
  { room: "Laundry", text: "Wipe washer/dryer exterior", task_type: "clean" },
];

export const DEEP_CLEAN_TASKS: TemplateTask[] = [...TURNOVER_CLEAN_TASKS, ...deepCleanExtras];

export const CHECKIN_PREP_TASKS: TemplateTask[] = [
  { room: "Living Room", text: "Visual check — tidy and guest-ready", task_type: "clean" },
  { room: "Living Room", text: "Fluff pillows and straighten throws", task_type: "clean" },
  { room: "Kitchen", text: "Visual check — counters clear, sink empty", task_type: "clean" },
  { room: "Kitchen", text: "Check coffee/tea supplies", task_type: "clean" },
  { room: "Master Bedroom", text: "Visual check — bed made, linens fresh", task_type: "clean" },
  { room: "Master Bedroom", text: "Ensure towels are folded and placed", task_type: "clean" },
  { room: "Guest Bedroom", text: "Visual check — bed made, linens fresh", task_type: "clean" },
  { room: "Guest Bedroom", text: "Ensure towels are folded and placed", task_type: "clean" },
  { room: "Master Bathroom", text: "Visual check — stocked and clean", task_type: "clean" },
  { room: "Guest Bathroom", text: "Visual check — stocked and clean", task_type: "clean" },
  { room: "Living Room", text: "Set out welcome items if provided", task_type: "clean" },
  { room: "General/Final", text: "Check AC/heat setting", task_type: "clean" },
  { room: "General/Final", text: "Final walkthrough all rooms", task_type: "clean" },
  { room: "General/Final", text: "Lock doors and set thermostat", task_type: "clean" },
  { room: "General/Final", text: "Take arrival-ready photos", task_type: "clean" },
  { room: "Kitchen", text: "Empty trash if needed", task_type: "clean" },
  { room: "Master Bathroom", text: "Restock toilet paper to minimum", task_type: "clean" },
  { room: "Guest Bathroom", text: "Restock toilet paper to minimum", task_type: "clean" },
  { room: "Laundry", text: "Confirm no laundry left in machines", task_type: "clean" },
  { room: "Living Room", text: "Wipe remotes and high-touch surfaces", task_type: "clean" },
  { room: "Kitchen", text: "Run dishwasher if dishes present", task_type: "clean" },
  { room: "Master Bedroom", text: "Check closet for guest items", task_type: "clean" },
  { room: "Guest Bedroom", text: "Check closet for guest items", task_type: "clean" },
  { room: "General/Final", text: "Note any maintenance issues", task_type: "clean" },
];

export const CHECKLIST_TEMPLATES: ChecklistTemplate[] = [
  {
    id: "turnover",
    name: "Turnover Clean",
    description:
      "Complete reset between guests. Bedrooms, bathrooms, kitchen, common areas.",
    tasks: TURNOVER_CLEAN_TASKS,
  },
  {
    id: "deep_clean",
    name: "Deep Clean",
    description: "Thorough clean including inside appliances, baseboards, windows.",
    tasks: DEEP_CLEAN_TASKS,
  },
  {
    id: "checkin_prep",
    name: "Check-in Prep",
    description: "Light touch-up before guest arrival. Staging and final checks.",
    tasks: CHECKIN_PREP_TASKS,
  },
];

export function getChecklistTemplate(id: string): ChecklistTemplate | undefined {
  return CHECKLIST_TEMPLATES.find((t) => t.id === id);
}

/** Case-insensitive substring match; falls back to first property room or template room name. */
export function matchTemplateRoomToProperty(
  templateRoom: string,
  propertyRooms: string[]
): string {
  if (propertyRooms.length === 0) return templateRoom;
  const lower = templateRoom.toLowerCase();
  const exact = propertyRooms.find((r) => r.toLowerCase() === lower);
  if (exact) return exact;
  const partial = propertyRooms.find(
    (r) =>
      r.toLowerCase().includes(lower) ||
      lower.includes(r.toLowerCase())
  );
  if (partial) return partial;
  if (lower.includes("general") || lower.includes("final")) {
    return propertyRooms[0];
  }
  return propertyRooms[0];
}

export function countTasksForRooms(
  templateId: string,
  propertyRooms: string[]
): number {
  const template = getChecklistTemplate(templateId);
  if (!template) return 0;
  const matchedRooms = new Set(
    template.tasks.map((t) => matchTemplateRoomToProperty(t.room, propertyRooms))
  );
  return template.tasks.length;
}
